import { Bell, BellOff, Clock, Globe } from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import NotificationService from "../services/NotificationService";
import { logger } from "../utils/logger";

const NotificationComponent = ({ user }) => {
  const [receivesNotifications, setReceivesNotifications] = useState(
    user.receives_notifications
  );
  const [preferredTime, setPreferredTime] = useState(
    user.notification_preferred_time
  );

  // Initialize timezone from user data or browser default
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    if (user.notification_timezone && user.notification_timezone !== "UTC") {
      return user.notification_timezone;
    }
    // Default to browser timezone
    try {
      return window.Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      return "UTC";
    }
  });

  const [status, setStatus] = useState("");
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Search functionality for timezone selector
  const [timezoneSearch, setTimezoneSearch] = useState("");
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [isTimezoneInputFocused, setIsTimezoneInputFocused] = useState(false);

  // Common timezone options for the dropdown
  const commonTimezones = [
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Toronto",
    "America/Vancouver",
    "America/Montreal",
    "America/Phoenix",
    "America/Anchorage",
    "America/Honolulu",
    "America/Sao_Paulo",
    "America/Mexico_City",
    "America/Argentina/Buenos_Aires",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Europe/Rome",
    "Europe/Madrid",
    "Europe/Amsterdam",
    "Europe/Brussels",
    "Europe/Vienna",
    "Europe/Prague",
    "Europe/Warsaw",
    "Europe/Stockholm",
    "Europe/Oslo",
    "Europe/Copenhagen",
    "Europe/Helsinki",
    "Europe/Athens",
    "Europe/Istanbul",
    "Europe/Moscow",
    "Europe/Kiev",
    "Asia/Tokyo",
    "Asia/Shanghai",
    "Asia/Beijing",
    "Asia/Hong_Kong",
    "Asia/Seoul",
    "Asia/Taipei",
    "Asia/Singapore",
    "Asia/Bangkok",
    "Asia/Jakarta",
    "Asia/Manila",
    "Asia/Kuala_Lumpur",
    "Asia/Ho_Chi_Minh",
    "Asia/Kolkata",
    "Asia/Mumbai",
    "Asia/Delhi",
    "Asia/Karachi",
    "Asia/Dhaka",
    "Asia/Dubai",
    "Asia/Tehran",
    "Asia/Baghdad",
    "Asia/Riyadh",
    "Asia/Jerusalem",
    "Australia/Sydney",
    "Australia/Melbourne",
    "Australia/Perth",
    "Australia/Brisbane",
    "Australia/Adelaide",
    "Australia/Darwin",
    "Australia/Hobart",
    "Pacific/Auckland",
    "Pacific/Fiji",
    "Pacific/Honolulu",
    "Pacific/Guam",
    "Africa/Cairo",
    "Africa/Lagos",
    "Africa/Nairobi",
    "Africa/Johannesburg",
    "Africa/Casablanca",
  ];

  // Filter timezones based on search
  const filteredTimezones = commonTimezones.filter((tz) =>
    tz.toLowerCase().includes(timezoneSearch.toLowerCase())
  );

  const handleTimezoneSelect = (timezone) => {
    setSelectedTimezone(timezone);
    setTimezoneSearch("");
    setShowTimezoneDropdown(false);
    setIsTimezoneInputFocused(false);
  };

  const handleTimezoneInputChange = (e) => {
    setTimezoneSearch(e.target.value);
    setShowTimezoneDropdown(true);
  };

  const handleTimezoneInputFocus = () => {
    setIsTimezoneInputFocused(true);
    setShowTimezoneDropdown(true);
  };

  const handleTimezoneInputBlur = () => {
    setIsTimezoneInputFocused(false);
    // Delay hiding to allow click on dropdown items
    setTimeout(() => setShowTimezoneDropdown(false), 150);
  };

  const clearTimezoneSearch = () => {
    setTimezoneSearch("");
    setShowTimezoneDropdown(false);
    setIsTimezoneInputFocused(false);
  };

  // Get display value for timezone input
  const getTimezoneDisplayValue = () => {
    if (isTimezoneInputFocused) {
      return timezoneSearch;
    }
    return timezoneSearch || selectedTimezone;
  };

  // Get user's browser timezone for display
  const browserTimezone = (() => {
    try {
      return window.Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      return "Unknown";
    }
  })();

  useEffect(() => {
    // Check if notifications are supported first
    const supportInfo = NotificationService.isPushNotificationSupported();

    if (!supportInfo.isSupported) {
      setIsLoading(false);
      setStatus("Push notifications are not supported in this browser");
      return;
    }

    const checkInitialSubscription = async () => {
      try {
        const hasSubscription = await NotificationService.checkSubscription();
        setReceivesNotifications(!!hasSubscription);

        if (hasSubscription) {
          setSubscription({ time: preferredTime });
        }
      } catch (error) {
        logger.error("Error checking subscription", {
          url: "notification check",
          error: error,
        });
        setStatus(`Error checking subscription: ${error.message}`);
        setReceivesNotifications(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkInitialSubscription();
  }, [user.receives_notifications, preferredTime]);

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      if (!receivesNotifications) {
        // When enabling notifications, automatically set timezone to browser timezone
        const browserTimezone = (() => {
          try {
            return window.Intl.DateTimeFormat().resolvedOptions().timeZone;
          } catch (error) {
            return "UTC";
          }
        })();
        
        await NotificationService.subscribe();
        setReceivesNotifications(true);
        setSelectedTimezone(browserTimezone);
        setSubscription({ time: preferredTime, timezone: browserTimezone });
        
        // Automatically update user preferences with browser timezone
        await NotificationService.updateSubscription(
          preferredTime,
          browserTimezone,
          user.id
        );
        
        setStatus("Notifications enabled successfully! Timezone set to your browser timezone.");
      } else {
        await NotificationService.unsubscribe();
        setReceivesNotifications(false);
        setSubscription(null);
        setStatus("Notifications disabled successfully!");
      }
    } catch (error) {
      logger.error("Error toggling notifications", {
        url: "notification toggle",
        error: error,
      });
      setStatus(`Error: ${error.message}`);
    }
    setIsLoading(false);
  };

  const handlePreferenceUpdate = async () => {
    setIsLoading(true);
    try {
      await NotificationService.updateSubscription(
        preferredTime,
        selectedTimezone,
        user.id
      );
      setStatus("Notification preferences updated successfully!");
      setSubscription({
        ...subscription,
        time: preferredTime,
        timezone: selectedTimezone,
      });
    } catch (error) {
      setStatus(`Error: ${error.message}`);
    }
    setIsLoading(false);
  };

  return (
    <div className="bg-white rounded shadow p-4">
      <div className="mb-4">
        <h2 className="mb-4">Notifications Preferences</h2>
        <div className="d-flex align-items-center justify-content-between p-3 border rounded">
          <div className="d-flex align-items-center gap-3">
            {receivesNotifications ? (
              <Bell size={20} className="text-pink-500" />
            ) : (
              <BellOff size={20} className="text-secondary" />
            )}
            <span>Notifications</span>
          </div>

          {isLoading ? (
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <div
              className="form-check form-switch"
              style={{ transform: "scale(1.5)", marginRight: "10px" }}
            >
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={receivesNotifications}
                onChange={handleToggle}
                style={{ cursor: "pointer" }}
              />
            </div>
          )}
        </div>
      </div>

      {receivesNotifications && (
        <div className="mb-4">
          <label className="form-label">Preferred Notification Time</label>
          <div className="input-group mb-3">
            <span className="input-group-text">
              <Clock size={20} className="text-secondary" />
            </span>
            <input
              type="time"
              className="form-control"
              value={preferredTime}
              onChange={(e) => setPreferredTime(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <label className="form-label">Timezone</label>
          <div className="input-group mb-3 position-relative">
            <span className="input-group-text">
              <Globe size={20} className="text-secondary" />
            </span>
            <input
              type="text"
              className="form-control"
              value={getTimezoneDisplayValue()}
              onChange={handleTimezoneInputChange}
              onFocus={handleTimezoneInputFocus}
              onBlur={handleTimezoneInputBlur}
              placeholder="Search for a timezone..."
              disabled={isLoading}
              autoComplete="off"
              style={{ fontSize: '16px' }} // Prevents zoom on iOS
            />
            {timezoneSearch && isTimezoneInputFocused && (
              <button
                type="button"
                className="btn btn-outline-secondary position-absolute"
                style={{ 
                  right: '5px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  zIndex: 5,
                  padding: '0.25rem 0.5rem',
                  border: 'none',
                  background: 'transparent'
                }}
                onClick={clearTimezoneSearch}
                onMouseDown={(e) => e.preventDefault()} // Prevents blur
              >
                ×
              </button>
            )}
            {showTimezoneDropdown && (
              <div 
                className="position-absolute w-100 bg-white border rounded shadow-lg"
                style={{ 
                  top: '100%', 
                  zIndex: 1000, 
                  maxHeight: window.innerHeight < 600 ? '150px' : '250px', 
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
                }}
              >
                {filteredTimezones.length > 0 ? (
                  <>
                    {/* Browser timezone suggestion */}
                    {!timezoneSearch && browserTimezone !== selectedTimezone && (
                      <>
                        <div
                          className="px-3 py-3 border-bottom"
                          style={{ 
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            backgroundColor: '#f8f9fa',
                            fontSize: '14px'
                          }}
                          onMouseDown={() => handleTimezoneSelect(browserTimezone)}
                          onTouchEnd={() => handleTimezoneSelect(browserTimezone)}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary">Detected</span>
                            <span>{browserTimezone}</span>
                          </div>
                        </div>
                      </>
                    )}
                    {filteredTimezones.map((tz) => (
                      <div
                        key={tz}
                        className="px-3 py-3"
                        style={{ 
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          minHeight: '48px', // Touch-friendly height
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '14px'
                        }}
                        onMouseDown={() => handleTimezoneSelect(tz)}
                        onTouchEnd={() => handleTimezoneSelect(tz)}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        {tz === selectedTimezone && (
                          <span className="me-2 text-success">✓</span>
                        )}
                        {tz}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-3 text-muted text-center" style={{ minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    No timezones found
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={handlePreferenceUpdate}
            disabled={isLoading}
          >
            {isLoading ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              />
            ) : (
              <Clock size={20} />
            )}
            Update Preferences
          </button>
        </div>
      )}

      {status && (
        <div
          className={`alert ${
            status.includes("Error") ? "alert-danger" : "alert-success"
          } mb-3`}
        >
          {status}
        </div>
      )}

      {subscription && (
        <div className="text-muted small">
          Your notifications are scheduled for {preferredTime} (
          {selectedTimezone})
        </div>
      )}
    </div>
  );
};

NotificationComponent.propTypes = {
  user: PropTypes.shape({
    receives_notifications: PropTypes.bool.isRequired,
    notification_preferred_time: PropTypes.string.isRequired,
    notification_timezone: PropTypes.string,
    id: PropTypes.number.isRequired,
  }).isRequired,
};

export default NotificationComponent;
